/**
 * Central translations module.
 *
 * ## Structure
 * - English translations are in translations-en.json (source of truth for keys)
 * - French translations are in translations-fr.json
 * - Only the user's selected language is loaded at runtime
 *
 * ## Adding a New Language
 * 1. Create a new `translations-XX.json` file following the English file structure
 * 2. Add the language to `SUPPORTED_LANGUAGES` below
 * 3. Add a loader to the `languageLoaders` map
 * 4. Run `yarn check-translations` to verify all keys are present
 *
 * ## Usage (in React components)
 * Prefer using the `useTranslation` hook from LanguageContext:
 * ```tsx
 * const { t, tPlural } = useTranslation();
 * return <span>{t('common.save')}</span>;
 * ```
 *
 * ## Usage (outside React)
 * For non-React code, use the direct functions:
 * ```ts
 * import { getTranslation } from 'src/i18n';
 * const label = getTranslation('common.save');
 * ```
 */

import translationsEnJson from './translations-en.json';

/**
 * Type representing all valid translation keys.
 * Derived from the English translations JSON (source of truth).
 */
export type TranslationKey = keyof typeof translationsEnJson;

// ============================================
// Language Configuration (Single Source of Truth)
// ============================================

/**
 * All supported languages with their metadata.
 * Add new languages here - this is the single source of truth.
 */
export const SUPPORTED_LANGUAGES = {
  en: { label: 'English', flag: '🇺🇸', nativeName: 'English' },
  fr: { label: 'French', flag: '🇫🇷', nativeName: 'Français' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

export const SUPPORTED_LANGUAGE_CODES = Object.keys(SUPPORTED_LANGUAGES) as SupportedLanguage[];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Check if a language code is supported.
 * Use this as a type guard when validating user input.
 *
 * @example
 * const userLang = navigator.language?.split('-')[0];
 * if (isValidLanguage(userLang)) {
 *   // userLang is now typed as SupportedLanguage
 *   initTranslations(userLang);
 * }
 */
export function isValidLanguage (lang: string): lang is SupportedLanguage {
  return lang in SUPPORTED_LANGUAGES;
}

// Dynamic loaders for each language - only imports when called
const languageLoaders: Record<SupportedLanguage, () => Promise<Record<string, TranslationValue>>> = {
  en: async () => (await import('./translations-en.json')).default as Record<string, TranslationValue>,
  fr: async () => (await import('./translations-fr.json')).default as Record<string, TranslationValue>,
};

/**
 * Plural translation value type.
 * Use this format for translations that need singular/plural forms.
 */
export interface PluralTranslation {
  singular: string;
  pl: string;
}

/**
 * Translation value type - can be a simple string or a plural object.
 */
export type TranslationValue = string | PluralTranslation;

/**
 * Type guard to check if a translation value is a plural object.
 */
export function isPluralTranslation (value: TranslationValue): value is PluralTranslation {
  return typeof value === 'object' && value !== null && 'singular' in value && 'pl' in value;
}

// Cached translations for the current language
let currentLanguage: SupportedLanguage = DEFAULT_LANGUAGE;
let currentTranslations: Record<string, TranslationValue> = {};
let isLoaded = false;

// Track warned keys to avoid console spam in development
const warnedMissingKeys = new Set<string>();

/**
 * Initialize translations for a specific language.
 * Call this once at app startup with the user's preferred language.
 *
 * @param language - The language to load
 * @returns Promise that resolves when translations are loaded
 */
export async function initTranslations (language: SupportedLanguage): Promise<void> {
  if (currentLanguage === language && isLoaded) {
    return; // Already loaded this language
  }

  const loader = languageLoaders[language] || languageLoaders[DEFAULT_LANGUAGE];
  currentTranslations = await loader();
  currentLanguage = language;
  isLoaded = true;
}


/**
 * Get the currently loaded language.
 */
export function getCurrentLanguage (): SupportedLanguage {
  return currentLanguage;
}

/**
 * Convert camelCase or snake_case to readable text.
 * Used as fallback when no translation is found.
 */
function formatKeyAsLabel (key: string): string {
  if (!key) return '';

  if (key.includes('_')) {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

/**
 * Try to match a sensor-related pattern and return the translation key + variables.
 * Returns null if no pattern matches.
 * Shared by getTranslation (for automatic fallback) and translateSensorSetting.
 */
function matchSensorPattern (key: string): { translationKey: string; sensorNumber: string } | null {
  // Handle temperatureSensors.N.settingName pattern
  const sensorMatch = key.match(/^temperatureSensors\.(\d+)\.(.+)$/);
  if (sensorMatch) {
    const [, sensorNumber, settingName] = sensorMatch;
    const normalizedSettingName = settingName.replace(/temperatureSource\d+FailLatching/, 'temperatureSourceFailLatching');
    return { translationKey: `temperatureSensors.${normalizedSettingName}`, sensorNumber };
  }

  // Handle lowTSNAlarmMask pattern
  const lowTsMatch = key.match(/^lowTS(\d+)AlarmMask$/);
  if (lowTsMatch) {
    return { translationKey: 'lowTSAlarmMask', sensorNumber: lowTsMatch[1] };
  }

  // Handle highTSNAlarmMask pattern
  const highTsMatch = key.match(/^highTS(\d+)AlarmMask$/);
  if (highTsMatch) {
    return { translationKey: 'highTSAlarmMask', sensorNumber: highTsMatch[1] };
  }

  // Handle tsNFailureAlarmMask pattern
  const tsFailureMatch = key.match(/^ts(\d+)FailureAlarmMask$/);
  if (tsFailureMatch) {
    return { translationKey: 'tsFailureAlarmMask', sensorNumber: tsFailureMatch[1] };
  }

  // Handle rtdNSettings pattern
  const rtdSettingsMatch = key.match(/^rtd(\d+)Settings$/);
  if (rtdSettingsMatch) {
    return { translationKey: 'rtdSettings', sensorNumber: rtdSettingsMatch[1] };
  }

  return null;
}

/**
 * Try to translate a key that matches sensor-related patterns.
 * Returns the translated string if a pattern matches, or null if no pattern matches.
 */
function trySensorPatternTranslation (key: string): string | null {
  const match = matchSensorPattern(key);
  if (match && currentTranslations[match.translationKey] !== undefined) {
    return getTranslation(match.translationKey, { variables: { sensorNumber: match.sensorNumber } });
  }
  return null;
}

/**
 * Options for translation retrieval.
 */
export interface TranslationOptions {
  /** Variables to interpolate into the translation (e.g., { name: 'John' }) */
  variables?: Record<string, string | number>;
  /** Count for pluralization - when provided, uses singular/plural forms */
  count?: number;
}

/**
 * Get a translation for a given key.
 * Uses the currently loaded language (call initTranslations first).
 * Falls back to formatting the key as a readable label if not found.
 *
 * @param key - A valid translation key (type-checked against TranslationKey)
 * @param options - Optional: variables for interpolation and/or count for pluralization
 * @returns The translated string, or a formatted fallback if not found
 *
 * @example
 * // Simple translation
 * getTranslation('common.save')
 *
 * @example
 * // With variable interpolation
 * // Translation: "Hello, {{name}}!"
 * getTranslation('greeting', { variables: { name: 'John' } })
 *
 * @example
 * // With pluralization
 * // Translation: { singular: '{{count}} Device', pl: '{{count}} Devices' }
 * getTranslation('devices.count', { count: 5 })
 *
 * @example
 * // With both
 * getTranslation('items.inCart', { count: 3, variables: { name: 'John' } })
 */
export function getTranslation (key: string, options?: TranslationOptions): string {
  if (!isLoaded) {
    // eslint-disable-next-line no-console
    console.warn('Translations not loaded. Call initTranslations() first.');
    return formatKeyAsLabel(key);
  }

  const translation = currentTranslations[key];

  if (translation === undefined) {
    // Try sensor pattern translation before falling back
    const sensorTranslation = trySensorPatternTranslation(key);
    if (sensorTranslation !== null) {
      return sensorTranslation;
    }

    // Warn in development to help catch missing translations
    if (process.env.NODE_ENV === 'development' && !warnedMissingKeys.has(key)) {
      warnedMissingKeys.add(key);
      // eslint-disable-next-line no-console
      console.warn(
        `[i18n] Missing translation for key: "${key}" (language: ${currentLanguage})`
      );
    }
    return formatKeyAsLabel(key);
  }

  // Handle pluralization
  if (options?.count !== undefined && isPluralTranslation(translation)) {
    const text = options.count === 1 ? translation.singular : translation.pl;
    const allVariables: Record<string, string | number> = { count: options.count, ...options.variables };
    return interpolateVariables(text, allVariables);
  }

  // Get the base text
  let text: string;
  if (isPluralTranslation(translation)) {
    text = translation.pl; // Default to plural form for plural translations without count
  } else {
    text = translation;
  }

  // Handle variable interpolation
  if (options?.variables) {
    return interpolateVariables(text, options.variables);
  }

  return text;
}

/**
 * Interpolate variables into a translation string.
 * Variables in the string should be in the format {{variableName}}.
 */
function interpolateVariables (
  text: string,
  variables: Record<string, string | number>
): string {
  let result = text;
  Object.entries(variables).forEach(([varName, value]) => {
    result = result.replace(new RegExp(`{{${varName}}}`, 'g'), String(value));
  });
  return result;
}

/**
 * Convert a key to a human-readable label.
 * Handles camelCase and snake_case conversion to Title Case.
 *
 * @example
 * langLookUpText('deviceStatus') // 'Device Status'
 * langLookUpText('device_status') // 'Device Status'
 */
export const langLookUpText = formatKeyAsLabel;

/**
 * Get a human-readable label for a device model name.
 * e.g., "NVent920" -> "NVent 920"
 */
export const getLabelForDeviceModel = (model: string): string => {
  if (!model) return '';

  return model
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2');
};

/**
 * Translates a temperature sensor setting path by extracting the sensor number.
 * Converts numbered paths like "temperatureSensors.3.lowTemperatureAlarmSetpoint"
 * to the simplified translation key "temperatureSensors.lowTemperatureAlarmSetpoint"
 * with the sensor number as a variable.
 *
 * Also handles alarm mask patterns like "lowTS3AlarmMask", "highTS5AlarmMask", etc.
 *
 * @param settingPath - The setting path (e.g., "temperatureSensors.3.lowTemperatureAlarmSetpoint")
 * @returns The translated string with the sensor number interpolated
 *
 * @example
 * translateSensorSetting('temperatureSensors.3.lowTemperatureAlarmSetpoint')
 * // Returns: "Temperature Sensor 3 Low Temperature Alarm Setpoint"
 *
 * @example
 * translateSensorSetting('lowTS2AlarmMask')
 * // Returns: "Low TS 2 Alarm Mask"
 *
 * @example
 * translateSensorSetting('rtd4Settings')
 * // Returns: "Temperature Sensor 4 Settings"
 */
export function translateSensorSetting (settingPath: string): string {
  const match = matchSensorPattern(settingPath);
  if (match) {
    return getTranslation(match.translationKey, { variables: { sensorNumber: match.sensorNumber } });
  }

  // Fallback to regular translation
  return getTranslation(settingPath);
}

// ============================================
// Synchronous API for backward compatibility
// ============================================

// Pre-load English for immediate availability (can be overridden by initTranslations)
currentTranslations = translationsEnJson as Record<string, TranslationValue>;
isLoaded = true;
